/**
 * Admin Shop Items — manage published cosmetics + mode Dots config.
 */
import {
  deleteAdminShopCosmetic,
  fetchAdminDotsConfig,
  fetchAdminShopCosmetics,
  patchAdminDotsConfig,
  patchAdminShopCosmetic,
  type AdminShopCosmeticRow,
  type CosmeticCategory,
  type ModeDotsConfigDto,
  type ShopUnlockType,
} from "../lib/cloud";
import {
  formatEndsInLabel,
  limitedShopPhase,
} from "../../lib/limitedDuration";

const CATEGORIES: Array<{ id: CosmeticCategory; label: string }> = [
  { id: "hat", label: "Hat" },
  { id: "face", label: "Face" },
  { id: "cursor", label: "Body" },
  { id: "cursor_color", label: "Cursor" },
  { id: "trail", label: "Trails" },
  { id: "banner", label: "Banners" },
  { id: "theme", label: "Themes" },
];

const MODES: Array<{ id: string; label: string }> = [
  { id: "normal", label: "Normal" },
  { id: "wasd", label: "WASD" },
  { id: "superdot", label: "Superdot" },
  { id: "progression", label: "Progression" },
  { id: "roulette", label: "Roulette" },
  { id: "sandbox", label: "Sandbox" },
  { id: "highway", label: "Highway" },
  { id: "darkness", label: "Darkness" },
  { id: "mirror", label: "Mirror" },
  { id: "snake", label: "Snake" },
  { id: "combo", label: "Combo" },
  { id: "overload", label: "Overload" },
  { id: "colorrush", label: "Color Rush" },
  { id: "heat", label: "Heat" },
  { id: "hunter", label: "Hunter" },
  { id: "pop", label: "POP" },
  { id: "counter", label: "Counter" },
  { id: "aim", label: "AIM" },
  { id: "hook", label: "Hook" },
  { id: "tilt", label: "Tilt" },
  { id: "orbit", label: "Orbit" },
  { id: "delay", label: "Delay" },
  { id: "penalty", label: "Golf" },
];

export function mountAdminShopItems(
  root: HTMLElement,
  opts?: {
    onEditItem?: (item: AdminShopCosmeticRow) => void;
    permissions?: { overwrite?: boolean; delete?: boolean };
  },
) {
  root.replaceChildren();

  const tabs = document.createElement("div");
  tabs.className = "adminShopItemsTabs modeBox";
  const tabItemsBtn = document.createElement("button");
  tabItemsBtn.type = "button";
  tabItemsBtn.className = "btn green";
  tabItemsBtn.textContent = "Shop Items";
  const tabSentBtn = document.createElement("button");
  tabSentBtn.type = "button";
  tabSentBtn.className = "btn";
  tabSentBtn.textContent = "Sent Items";
  const tabLimitedBtn = document.createElement("button");
  tabLimitedBtn.type = "button";
  tabLimitedBtn.className = "btn";
  tabLimitedBtn.textContent = "Limited Items";
  const tabDotsBtn = document.createElement("button");
  tabDotsBtn.type = "button";
  tabDotsBtn.className = "btn";
  tabDotsBtn.textContent = "Dots conversion";
  tabs.appendChild(tabItemsBtn);
  tabs.appendChild(tabSentBtn);
  tabs.appendChild(tabLimitedBtn);
  tabs.appendChild(tabDotsBtn);
  root.appendChild(tabs);

  const itemsPanel = document.createElement("div");
  itemsPanel.className = "adminShopItemsPanel";
  const sentPanel = document.createElement("div");
  sentPanel.className = "adminShopSentPanel hidden";
  const limitedPanel = document.createElement("div");
  limitedPanel.className = "adminShopLimitedPanel hidden";
  const dotsPanel = document.createElement("div");
  dotsPanel.className = "adminShopDotsPanel hidden";

  const itemsStatus = document.createElement("p");
  itemsStatus.className = "accessHint";
  const itemsWrap = document.createElement("div");
  itemsWrap.className = "leaderboardWrap adminShopItemsWrap";
  itemsPanel.appendChild(itemsStatus);
  itemsPanel.appendChild(itemsWrap);

  const sentStatus = document.createElement("p");
  sentStatus.className = "accessHint";
  const sentWrap = document.createElement("div");
  sentWrap.className = "leaderboardWrap adminShopItemsWrap";
  sentPanel.appendChild(sentStatus);
  sentPanel.appendChild(sentWrap);

  const limitedStatus = document.createElement("p");
  limitedStatus.className = "accessHint";
  const limitedWrap = document.createElement("div");
  limitedWrap.className = "leaderboardWrap adminShopItemsWrap";
  limitedPanel.appendChild(limitedStatus);
  limitedPanel.appendChild(limitedWrap);

  const dotsStatus = document.createElement("p");
  dotsStatus.className = "accessHint";
  const dotsWrap = document.createElement("div");
  dotsWrap.className = "leaderboardWrap";
  dotsPanel.appendChild(dotsStatus);
  dotsPanel.appendChild(dotsWrap);

  root.appendChild(itemsPanel);
  root.appendChild(sentPanel);
  root.appendChild(limitedPanel);
  root.appendChild(dotsPanel);

  let items: AdminShopCosmeticRow[] = [];
  let dotsConfig: ModeDotsConfigDto[] = [];

  function renderLimitedItems() {
    limitedWrap.replaceChildren();
    const limited = items.filter((i) => !!i.limitedItem && !i.crateOnly && !i.sentOnly);
    if (!limited.length) {
      limitedWrap.textContent = "No limited cosmetics.";
      return;
    }
    const active = limited
      .filter((i) => limitedShopPhase(i) === "active")
      .sort((a, b) => String(a.limitedEndsAt || "").localeCompare(String(b.limitedEndsAt || "")));
    const ended = limited
      .filter((i) => {
        const p = limitedShopPhase(i);
        return p === "cooldown" || p === "expired";
      })
      .sort((a, b) => String(b.limitedEndsAt || "").localeCompare(String(a.limitedEndsAt || "")));

    const appendSection = (title: string, rows: AdminShopCosmeticRow[]) => {
      const h = document.createElement("h3");
      h.className = "adminShopLimitedSectionTitle";
      h.textContent = `${title} (${rows.length})`;
      limitedWrap.appendChild(h);
      if (!rows.length) {
        const empty = document.createElement("p");
        empty.className = "accessHint";
        empty.textContent = "None.";
        limitedWrap.appendChild(empty);
        return;
      }
      const table = document.createElement("table");
      table.className = "leaderboardTable adminShopItemsTable";
      table.innerHTML = `<thead><tr><th>Name</th><th>Duration</th><th>Ends / Ended</th><th>Status</th><th>Actions</th></tr></thead>`;
      const tbody = document.createElement("tbody");
      for (const item of rows) {
        const tr = document.createElement("tr");
        const phase = limitedShopPhase(item);
        const tdName = document.createElement("td");
        tdName.textContent = item.name;
        const tdDur = document.createElement("td");
        tdDur.textContent = item.limitedDurationText || "—";
        const tdEnds = document.createElement("td");
        if (item.limitedEndsAt) {
          const endsMs = Date.parse(item.limitedEndsAt);
          tdEnds.textContent = Number.isFinite(endsMs)
            ? new Date(endsMs).toLocaleString()
            : item.limitedEndsAt;
        } else tdEnds.textContent = "—";
        const tdStatus = document.createElement("td");
        if (phase === "active") {
          tdStatus.textContent = `Active — ends in ${formatEndsInLabel(item.limitedEndsAt || "")}`;
        } else if (phase === "cooldown") {
          tdStatus.textContent = "Ended (cooldown)";
        } else {
          tdStatus.textContent = "Ended";
        }
        const tdAct = document.createElement("td");
        const editBtn = document.createElement("button");
        editBtn.type = "button";
        editBtn.className = "btn";
        editBtn.textContent = "Edit";
        editBtn.onclick = () => opts?.onEditItem?.(item);
        tdAct.appendChild(editBtn);
        tr.appendChild(tdName);
        tr.appendChild(tdDur);
        tr.appendChild(tdEnds);
        tr.appendChild(tdStatus);
        tr.appendChild(tdAct);
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      limitedWrap.appendChild(table);
    };

    appendSection("Active (not ended)", active);
    appendSection("Ended", ended);
  }

  async function loadItems() {
    itemsStatus.textContent = "Loading…";
    sentStatus.textContent = "Loading…";
    limitedStatus.textContent = "Loading…";
    try {
      items = await fetchAdminShopCosmetics();
      renderItems();
      renderSentItems();
      renderLimitedItems();
      const shopCount = items.filter((i) => !i.crateOnly && !i.sentOnly).length;
      const sentCount = items.filter((i) => !!i.sentOnly).length;
      itemsStatus.textContent = `${shopCount} shop cosmetic(s).`;
      sentStatus.textContent = `${sentCount} sent item(s).`;
      const limitedCount = items.filter((i) => !!i.limitedItem && !i.crateOnly && !i.sentOnly).length;
      limitedStatus.textContent = `${limitedCount} limited cosmetic(s).`;
    } catch (e) {
      itemsStatus.textContent = e instanceof Error ? e.message : "Load failed.";
      sentStatus.textContent = itemsStatus.textContent;
      limitedStatus.textContent = itemsStatus.textContent;
    }
  }

  function openPublishSentModal(item: AdminShopCosmeticRow) {
    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox";
    box.innerHTML = `<h2>Publish “${item.name}”</h2>`;

    const destSelect = document.createElement("select");
    destSelect.className = "formInput";
    [
      ["shop", "Publish to Shop"],
      ["crates", "Publish to Crates"],
    ].forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      destSelect.appendChild(o);
    });

    const unlockSelect = document.createElement("select");
    unlockSelect.className = "formInput";
    [
      ["dots", "Price in Dots"],
      ["free", "Free"],
      ["achievement", "Achievement"],
      ["challenge", "Challenge"],
      ["score", "Score in mode"],
    ].forEach(([v, label]) => {
      const o = document.createElement("option");
      o.value = v;
      o.textContent = label;
      unlockSelect.appendChild(o);
    });
    unlockSelect.value = "dots";

    const priceInput = document.createElement("input");
    priceInput.type = "number";
    priceInput.min = "0";
    priceInput.className = "formInput";
    priceInput.value = "100";

    const shopFields = document.createElement("div");
    const err = document.createElement("p");
    err.className = "accessHint";
    const sync = () => {
      shopFields.classList.toggle("hidden", destSelect.value !== "shop");
      priceInput.classList.toggle("hidden", unlockSelect.value !== "dots");
    };
    destSelect.onchange = sync;
    unlockSelect.onchange = sync;

    const row = (label: string, el: HTMLElement, parent: HTMLElement = box) => {
      const fr = document.createElement("div");
      fr.className = "formRow";
      const sp = document.createElement("span");
      sp.className = "formLabel";
      sp.textContent = label;
      fr.appendChild(sp);
      fr.appendChild(el);
      parent.appendChild(fr);
    };
    row("Destination", destSelect);
    row("Unlock", unlockSelect, shopFields);
    row("Price", priceInput, shopFields);
    box.appendChild(shopFields);
    box.appendChild(err);
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.className = "btn green";
    okBtn.textContent = "Publish";
    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);
    sync();
    cancelBtn.onclick = () => modal.remove();
    okBtn.onclick = async () => {
      err.textContent = "Publishing…";
      try {
        if (destSelect.value === "crates") {
          await patchAdminShopCosmetic({
            id: item.id,
            sentOnly: false,
            crateOnly: true,
            unlockType: "free",
            limitedItem: false,
            notifyShop: false,
          });
          modal.remove();
          sentStatus.textContent = `"${item.name}" moved to Crates Items.`;
        } else {
          const unlockType = unlockSelect.value as ShopUnlockType;
          await patchAdminShopCosmetic({
            id: item.id,
            sentOnly: false,
            crateOnly: false,
            unlockType,
            priceDots: unlockType === "dots" ? Number(priceInput.value) : undefined,
            limitedItem: false,
            notifyShop: true,
          });
          modal.remove();
          sentStatus.textContent = `"${item.name}" published to Shop.`;
        }
        await loadItems();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "Publish failed.";
      }
    };
  }

  function renderSentItems() {
    sentWrap.replaceChildren();
    const sent = items.filter((i) => !!i.sentOnly);
    if (!sent.length) {
      sentWrap.textContent = "No sent items. Use Shop Creator → Publish → Send to.";
      return;
    }
    const table = document.createElement("table");
    table.className = "leaderboardTable adminShopItemsTable";
    table.innerHTML = `<thead><tr><th>Name</th><th>Category</th><th>Recipients</th><th>Actions</th></tr></thead>`;
    const tbody = document.createElement("tbody");
    for (const item of sent) {
      const tr = document.createElement("tr");
      const tdName = document.createElement("td");
      tdName.textContent = item.name;
      const tdCat = document.createElement("td");
      tdCat.textContent =
        CATEGORIES.find((c) => c.id === item.category)?.label ?? item.category ?? "—";
      const tdRec = document.createElement("td");
      tdRec.textContent = String(item.recipientCount ?? 0);

      const editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.className = "btn";
      editBtn.textContent = "Edit";
      editBtn.onclick = () => {
        if (!opts?.onEditItem) {
          sentStatus.textContent = "Shop Creator is not available.";
          return;
        }
        opts.onEditItem(item);
      };

      const publishBtn = document.createElement("button");
      publishBtn.type = "button";
      publishBtn.className = "btn green";
      publishBtn.textContent = "Publish";
      if (opts?.permissions?.overwrite === false) {
        publishBtn.disabled = true;
        publishBtn.title = "Missing Overwrite Shop Items permission";
      }
      publishBtn.onclick = () => openPublishSentModal(item);

      const revokeBtn = document.createElement("button");
      revokeBtn.type = "button";
      revokeBtn.className = "btn";
      revokeBtn.textContent = "Delete for all";
      revokeBtn.title = "Remove from all inventories; keep in Sent Items list";
      if (opts?.permissions?.delete === false && opts?.permissions?.overwrite === false) {
        revokeBtn.disabled = true;
      }
      revokeBtn.onclick = async () => {
        if (!confirm(`Remove “${item.name}” from all users’ inventories? It stays in Sent Items.`)) {
          return;
        }
        sentStatus.textContent = "Revoking…";
        try {
          const res = await patchAdminShopCosmetic({ id: item.id, revokeOwners: true });
          sentStatus.textContent = `Removed from ${res.revoked ?? 0} user(s).`;
          await loadItems();
        } catch (e) {
          sentStatus.textContent = e instanceof Error ? e.message : "Revoke failed.";
        }
      };

      const deleteItemBtn = document.createElement("button");
      deleteItemBtn.type = "button";
      deleteItemBtn.className = "btn";
      deleteItemBtn.textContent = "Delete Item";
      deleteItemBtn.title = "Permanently delete from Sent Items, catalog, and all users";
      if (opts?.permissions?.delete === false) {
        deleteItemBtn.disabled = true;
        deleteItemBtn.title = "Missing Delete Shop Items permission";
      }
      deleteItemBtn.onclick = async () => {
        if (
          !confirm(
            `Permanently delete “${item.name}”? It will be removed from Sent Items, the catalog, and every user’s inventory.`,
          )
        ) {
          return;
        }
        sentStatus.textContent = "Deleting…";
        try {
          const res = await deleteAdminShopCosmetic(item.id);
          sentStatus.textContent = `Deleted permanently. Owners cleared: ${res.refundsProcessed ?? 0}.`;
          await loadItems();
        } catch (e) {
          sentStatus.textContent = e instanceof Error ? e.message : "Delete failed.";
        }
      };

      const tdAct = document.createElement("td");
      tdAct.appendChild(editBtn);
      tdAct.appendChild(publishBtn);
      tdAct.appendChild(revokeBtn);
      tdAct.appendChild(deleteItemBtn);
      tr.appendChild(tdName);
      tr.appendChild(tdCat);
      tr.appendChild(tdRec);
      tr.appendChild(tdAct);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    sentWrap.appendChild(table);
  }

  function renderItems() {
    itemsWrap.replaceChildren();
    const shopItems = items.filter((i) => !i.crateOnly && !i.sentOnly && !i.campaignOnly);
    if (!shopItems.length) {
      itemsWrap.textContent = "No published cosmetics.";
      return;
    }
    const table = document.createElement("table");
    table.className = "leaderboardTable adminShopItemsTable";
    table.innerHTML = `<thead><tr><th>Name</th><th>Category</th><th>Unlock</th><th>Options</th><th>Actions</th></tr></thead>`;
    const tbody = document.createElement("tbody");
    for (const item of shopItems) {
      const tr = document.createElement("tr");
      const nameInput = document.createElement("textarea");
      nameInput.className = "formInput adminShopItemName";
      nameInput.rows = 2;
      nameInput.value = item.name;

      const categorySelect = document.createElement("select");
      categorySelect.className = "formInput";
      CATEGORIES.forEach(({ id, label }) => {
        const o = document.createElement("option");
        o.value = id;
        o.textContent = label;
        categorySelect.appendChild(o);
      });
      categorySelect.value = item.category || "cursor";
      if (item.newCursor) {
        categorySelect.value = "cursor_color";
        categorySelect.disabled = true;
        categorySelect.title = "Locked to Cursor (New Cursor item).";
      }

      const unlockSelect = document.createElement("select");
      unlockSelect.className = "formInput";
      ["dots", "achievement", "challenge", "score"].forEach((v) => {
        const o = document.createElement("option");
        o.value = v;
        o.textContent = v;
        unlockSelect.appendChild(o);
      });
      unlockSelect.value = item.unlockType;

      const priceInput = document.createElement("input");
      priceInput.type = "number";
      priceInput.min = "0";
      priceInput.className = "formInput";
      priceInput.value = String(item.priceDots ?? 0);
      priceInput.style.display = item.unlockType === "dots" ? "" : "none";

      unlockSelect.onchange = () => {
        priceInput.style.display = unlockSelect.value === "dots" ? "" : "none";
      };

      const customColorCheck = document.createElement("input");
      customColorCheck.type = "checkbox";
      customColorCheck.checked = !!item.allowCustomColor;
      const customColorLab = document.createElement("label");
      customColorLab.className = "adminShopCheckLabel";
      customColorLab.appendChild(customColorCheck);
      customColorLab.appendChild(document.createTextNode(" Custom color"));

      const repurchaseCheck = document.createElement("input");
      repurchaseCheck.type = "checkbox";
      repurchaseCheck.checked = !!item.allowRepurchase;
      const repurchaseLab = document.createElement("label");
      repurchaseLab.className = "adminShopCheckLabel";
      repurchaseLab.appendChild(repurchaseCheck);
      repurchaseLab.appendChild(document.createTextNode(" Repurchase"));

      const matchCursorCheck = document.createElement("input");
      matchCursorCheck.type = "checkbox";
      matchCursorCheck.checked = !!item.matchCursorColor;
      const matchCursorLab = document.createElement("label");
      matchCursorLab.className = "adminShopCheckLabel";
      matchCursorLab.appendChild(matchCursorCheck);
      matchCursorLab.appendChild(document.createTextNode(" Match cursor color"));

      const shadeInput = document.createElement("input");
      shadeInput.type = "range";
      shadeInput.min = "-100";
      shadeInput.max = "100";
      shadeInput.step = "1";
      shadeInput.className = "adminShopShadeSlider";
      const shadeInit =
        typeof item.cursorColorShade === "number" && Number.isFinite(item.cursorColorShade)
          ? Math.round(Math.max(-1, Math.min(1, item.cursorColorShade)) * 100)
          : 0;
      shadeInput.value = String(shadeInit);
      shadeInput.classList.toggle("hidden", !matchCursorCheck.checked);
      matchCursorCheck.onchange = () => {
        shadeInput.classList.toggle("hidden", !matchCursorCheck.checked);
      };

      const optsWrap = document.createElement("div");
      optsWrap.className = "adminShopItemOpts";
      optsWrap.appendChild(customColorLab);
      optsWrap.appendChild(repurchaseLab);
      optsWrap.appendChild(matchCursorLab);
      optsWrap.appendChild(shadeInput);

      const saveBtn = document.createElement("button");
      saveBtn.type = "button";
      saveBtn.className = "btn green";
      saveBtn.textContent = "Save";
      saveBtn.onclick = async () => {
        itemsStatus.textContent = "Saving…";
        try {
          const nextUnlock = unlockSelect.value as AdminShopCosmeticRow["unlockType"];
          await patchAdminShopCosmetic({
            id: item.id,
            name: nameInput.value.trim(),
            category: item.newCursor
              ? "cursor_color"
              : (categorySelect.value as CosmeticCategory),
            allowCustomColor: customColorCheck.checked,
            allowRepurchase: repurchaseCheck.checked,
            matchCursorColor: matchCursorCheck.checked,
            cursorColorShade: matchCursorCheck.checked
              ? Math.max(-1, Math.min(1, (Number(shadeInput.value) || 0) / 100))
              : 0,
            unlockType: nextUnlock,
            priceDots: nextUnlock === "dots" ? Number(priceInput.value) : undefined,
            unlockAchievementId:
              nextUnlock === "achievement" ? item.unlockAchievementId ?? undefined : undefined,
            unlockChallengeId:
              nextUnlock === "challenge" ? item.unlockChallengeId ?? undefined : undefined,
            unlockScoreMode:
              nextUnlock === "score" ? item.unlockScoreMode ?? undefined : undefined,
            unlockScoreValue:
              nextUnlock === "score"
                ? item.unlockScoreValue ?? undefined
                : undefined,
            newCursor: !!item.newCursor,
          });
          itemsStatus.textContent = "Saved.";
          await loadItems();
        } catch (e) {
          itemsStatus.textContent = e instanceof Error ? e.message : "Save failed.";
        }
      };

      const editBtn = document.createElement("button");
      editBtn.type = "button";
      editBtn.className = "btn";
      editBtn.textContent = "Edit";
      editBtn.onclick = () => {
        if (!opts?.onEditItem) {
          itemsStatus.textContent = "Shop Creator is not available.";
          return;
        }
        opts.onEditItem({
          ...item,
          name: nameInput.value.trim() || item.name,
          category: categorySelect.value as CosmeticCategory,
          allowCustomColor: customColorCheck.checked,
          allowRepurchase: repurchaseCheck.checked,
          matchCursorColor: matchCursorCheck.checked,
          cursorColorShade: matchCursorCheck.checked
            ? Math.max(-1, Math.min(1, (Number(shadeInput.value) || 0) / 100))
            : 0,
          unlockType: unlockSelect.value as AdminShopCosmeticRow["unlockType"],
          priceDots: unlockSelect.value === "dots" ? Number(priceInput.value) : item.priceDots,
        });
      };

      const delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "btn";
      delBtn.textContent = "Delete";
      if (opts?.permissions?.delete === false) {
        delBtn.disabled = true;
        delBtn.title = "Missing Delete Shop Items permission";
      }
      delBtn.onclick = async () => {
        if (opts?.permissions?.delete === false) {
          itemsStatus.textContent = "Missing Delete Shop Items permission.";
          return;
        }
        if (!confirm(`Delete "${item.name}"? Purchasers will be refunded.`)) return;
        itemsStatus.textContent = "Deleting…";
        try {
          const res = await deleteAdminShopCosmetic(item.id);
          itemsStatus.textContent = `Deleted. Refunds processed: ${res.refundsProcessed ?? 0}.`;
          await loadItems();
        } catch (e) {
          itemsStatus.textContent = e instanceof Error ? e.message : "Delete failed.";
        }
      };

      const tdName = document.createElement("td");
      tdName.appendChild(nameInput);
      const tdCat = document.createElement("td");
      tdCat.appendChild(categorySelect);
      const tdUnlock = document.createElement("td");
      tdUnlock.appendChild(unlockSelect);
      tdUnlock.appendChild(priceInput);
      const tdOpts = document.createElement("td");
      tdOpts.appendChild(optsWrap);
      const tdAct = document.createElement("td");
      tdAct.appendChild(editBtn);
      tdAct.appendChild(saveBtn);
      tdAct.appendChild(delBtn);
      tr.appendChild(tdName);
      tr.appendChild(tdCat);
      tr.appendChild(tdUnlock);
      tr.appendChild(tdOpts);
      tr.appendChild(tdAct);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    itemsWrap.appendChild(table);
  }

  async function loadDotsConfig() {
    dotsStatus.textContent = "Loading…";
    try {
      dotsConfig = await fetchAdminDotsConfig();
      renderDotsConfig();
      dotsStatus.textContent = "Per-mode score → Dots conversion.";
    } catch (e) {
      dotsStatus.textContent = e instanceof Error ? e.message : "Load failed.";
    }
  }

  function renderDotsConfig() {
    dotsWrap.replaceChildren();
    const table = document.createElement("table");
    table.className = "leaderboardTable";
    table.innerHTML = `<thead><tr><th>Mode</th><th>Conversion rate</th><th>Bonus base</th><th></th></tr></thead>`;
    const tbody = document.createElement("tbody");
    for (const row of dotsConfig) {
      const label = MODES.find((m) => m.id === row.modeId)?.label ?? row.modeId;
      const rateInput = document.createElement("input");
      rateInput.type = "number";
      rateInput.min = "0";
      rateInput.className = "formInput";
      rateInput.value = String(row.dotsConversionRate);
      rateInput.title = "Score points per Dot. 0 = no Dots from score in this mode.";
      const bonusInput = document.createElement("input");
      bonusInput.type = "number";
      bonusInput.min = "0";
      bonusInput.className = "formInput";
      bonusInput.value = String(row.dotsBonusBase);
      const saveBtn = document.createElement("button");
      saveBtn.type = "button";
      saveBtn.className = "btn green";
      saveBtn.textContent = "Save";
      saveBtn.onclick = async () => {
        dotsStatus.textContent = "Saving…";
        try {
          await patchAdminDotsConfig({
            modeId: row.modeId,
            dotsConversionRate: Number(rateInput.value),
            dotsBonusBase: Number(bonusInput.value),
          });
          dotsStatus.textContent = `Saved ${label}.`;
          await loadDotsConfig();
        } catch (e) {
          dotsStatus.textContent = e instanceof Error ? e.message : "Save failed.";
        }
      };
      const tr = document.createElement("tr");
      const tdMode = document.createElement("td");
      tdMode.textContent = label;
      tr.appendChild(tdMode);
      const tdRate = document.createElement("td");
      tdRate.appendChild(rateInput);
      tr.appendChild(tdRate);
      const tdBonus = document.createElement("td");
      tdBonus.appendChild(bonusInput);
      tr.appendChild(tdBonus);
      const tdAct = document.createElement("td");
      tdAct.appendChild(saveBtn);
      tr.appendChild(tdAct);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    dotsWrap.appendChild(table);
  }

  const showPanel = (which: "items" | "sent" | "limited" | "dots") => {
    tabItemsBtn.classList.toggle("green", which === "items");
    tabSentBtn.classList.toggle("green", which === "sent");
    tabLimitedBtn.classList.toggle("green", which === "limited");
    tabDotsBtn.classList.toggle("green", which === "dots");
    itemsPanel.classList.toggle("hidden", which !== "items");
    sentPanel.classList.toggle("hidden", which !== "sent");
    limitedPanel.classList.toggle("hidden", which !== "limited");
    dotsPanel.classList.toggle("hidden", which !== "dots");
  };

  tabItemsBtn.onclick = () => {
    showPanel("items");
    void loadItems();
  };
  tabSentBtn.onclick = () => {
    showPanel("sent");
    void loadItems();
  };
  tabLimitedBtn.onclick = () => {
    showPanel("limited");
    void loadItems();
  };
  tabDotsBtn.onclick = () => {
    showPanel("dots");
    void loadDotsConfig();
  };

  void loadItems();
}
